import React, { useEffect, useMemo, useRef, useState } from "react";
import "./FoodRadarHomePage.css";
import FoodRadarForegroundNotification from "./FoodRadarForegroundNotification";

const getVendorImageSrc = (vendor) => {
  const rawValue =
    vendor?.shopImageUrl ||
    vendor?.shopLogoUrl ||
    vendor?.shopImage ||
    vendor?.shopLogo ||
    "";

  if (!rawValue || typeof rawValue !== "string") {
    return null;
  }

  let imageValue = rawValue.trim();

  try {
    const parsed = JSON.parse(imageValue);
    if (Array.isArray(parsed) && parsed.length > 0) {
      imageValue = String(parsed[0] || "").trim();
    } else if (typeof parsed === "string") {
      imageValue = parsed.trim();
    } else if (parsed && typeof parsed === "object") {
      imageValue = String(
        parsed.url ||
          parsed.path ||
          parsed.image ||
          parsed.filename ||
          parsed.file ||
          ""
      ).trim();
    }
  } catch (error) {
    // Normal string path, no action needed.
  }

  if (!imageValue) {
    return null;
  }

  if (
    imageValue.startsWith("http://") ||
    imageValue.startsWith("https://") ||
    imageValue.startsWith("data:")
  ) {
    return imageValue;
  }

  if (imageValue.startsWith("/")) {
    return imageValue;
  }

  if (imageValue.startsWith("uploads/")) {
    return `/${imageValue}`;
  }

  return `/uploads/${imageValue}`;
};


const safeNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const getVendorId = (vendor) =>
  Number(vendor?.vendorId || vendor?.vendor_id || vendor?.id || 0);


const renderRatingStars = (ratingValue) => {
  const rating = Math.max(0, Math.min(5, Number(ratingValue || 0)));
  const filledStars = Math.round(rating);
  const emptyStars = Math.max(0, 5 - filledStars);

  return `${"★".repeat(filledStars)}${"☆".repeat(emptyStars)}`;
};

const loadVendorRealStats = async (vendor, customerId) => {
  const vendorId = getVendorId(vendor);
  if (!vendorId) return vendor;

  try {
    const params = new URLSearchParams();

    if (customerId) {
      params.set("customerId", customerId);
      params.set("customer_id", customerId);
    }

    const query = params.toString() ? `?${params.toString()}` : "";

    const response = await fetch(
      `/api/public/food-radar/vendor-stats/${vendorId}${query}`
    );

    const data = await response.json();

    if (!response.ok || !data?.success || !data?.vendor) {
      return vendor;
    }

    const stats = data.vendor;

    return {
      ...vendor,
      average_rating: safeNumber(stats.average_rating, 0),
      rating_count: Math.max(0, parseInt(stats.rating_count || 0, 10) || 0),
      available_coins:
        stats.available_coins === null || stats.available_coins === undefined
          ? null
          : Math.max(0, parseInt(stats.available_coins || 0, 10) || 0),
      pending_coins:
        stats.pending_coins === null || stats.pending_coins === undefined
          ? null
          : Math.max(0, parseInt(stats.pending_coins || 0, 10) || 0),
    };
  } catch (error) {
    console.error(`Food Radar vendor stats load error for vendor ${vendorId}:`, error);
    return vendor;
  }
};

function FoodRadarHomePage() {
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("All");
  const [locationStatus, setLocationStatus] = useState("Location Required");
  const [locationRequired, setLocationRequired] = useState(true);
  const [vendors, setVendors] = useState([]);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [initialLocationLoading, setInitialLocationLoading] = useState(true);
  const [customerLocation, setCustomerLocation] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [profileForm, setProfileForm] = useState({
    name: "",
    mobile: "",
    email: "",
    default_address: "",
  });

  const locationRequestStartedRef = useRef(false);
  const locationRetryTimerRef = useRef(null);

  const filters = ["All", "Pure Veg", "Offers", "Open Now", "Delivery", "Under ₹99"];

  useEffect(() => {
    let manifestLink = document.querySelector('link[rel="manifest"]');

    if (!manifestLink) {
      manifestLink = document.createElement("link");
      manifestLink.setAttribute("rel", "manifest");
      document.head.appendChild(manifestLink);
    }

    const oldManifest = manifestLink.getAttribute("href");
    const oldTitle = document.title;

    manifestLink.setAttribute("href", "/manifest-food-radar.json");
    document.title = "VAKRA Food Radar";

    return () => {
      manifestLink.setAttribute("href", oldManifest || "/manifest.json");
      document.title = oldTitle || "VAKRA";
    };
  }, []);

  const loadVendors = async (location) => {
    if (!location?.latitude || !location?.longitude) {
      setVendors([]);
      setLocationRequired(true);
      setLocationStatus("Location Required");
      return;
    }

    try {
      setLoadingVendors(true);

      const query = `?lat=${location.latitude}&lng=${location.longitude}`;
      const response = await fetch(`/api/public/food-radar/vendors${query}`);
      const data = await response.json();

      if (data.success && Array.isArray(data.vendors)) {
        const baseVendors = data.vendors.length > 0 ? data.vendors : [];

        const savedCustomerId =
          localStorage.getItem("foodRadarCustomerId") || "";

        const vendorsWithRealStats = await Promise.all(
          baseVendors.map((vendor) =>
            loadVendorRealStats(vendor, savedCustomerId)
          )
        );

        setVendors(vendorsWithRealStats);
      } else {
        setVendors([]);
      }
    } catch (error) {
      console.error("Food Radar vendors load error:", error);
      setVendors([]);
    } finally {
      setLoadingVendors(false);
    }
  };

  const syncCustomerCurrentLocation = async (location) => {
    const savedCustomerId = localStorage.getItem("foodRadarCustomerId");

    if (!savedCustomerId || !location?.latitude || !location?.longitude) {
      return;
    }

    try {
      const response = await fetch(
        `/api/public/food-radar/customers/${savedCustomerId}/location`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            latitude: location.latitude,
            longitude: location.longitude,
          }),
        }
      );

      if (!response.ok) {
        console.warn("Food Radar customer location sync failed.");
      }
    } catch (error) {
      console.warn("Food Radar customer location sync error:", error);
    }
  };

  const requestCurrentLocation = (silent = false) => {
    if (locationRequestStartedRef.current) {
      return;
    }

    if (!navigator.geolocation) {
      setInitialLocationLoading(false);
      setLoadingVendors(false);
      setLocationRequired(true);
      setLocationStatus("Location Required");
      setVendors([]);

      if (!silent) {
        alert("Please allow your location to show nearby food offers.");
      }

      return;
    }

    locationRequestStartedRef.current = true;
    setInitialLocationLoading(true);
    setLoadingVendors(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        locationRequestStartedRef.current = false;

        const location = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };

        setCustomerLocation(location);
        setLocationStatus("Current Location");
        setLocationRequired(false);

        syncCustomerCurrentLocation(location);
        await loadVendors(location);
        setInitialLocationLoading(false);
      },
      () => {
        locationRequestStartedRef.current = false;
        setInitialLocationLoading(false);
        setLoadingVendors(false);
        setCustomerLocation(null);
        setVendors([]);
        setLocationRequired(true);
        setLocationStatus("Location Required");

        if (!silent) {
          alert("Please allow your location to show nearby food offers.");
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      }
    );
  };

  useEffect(() => {
    const savedCustomerId = localStorage.getItem("foodRadarCustomerId");
    const savedCustomerMobile = localStorage.getItem("foodRadarCustomerMobile");

    if (savedCustomerId && savedCustomerMobile) {
      setCustomer({
        id: savedCustomerId,
        mobile: savedCustomerMobile,
      });

      setProfileForm((prev) => ({
        ...prev,
        mobile: savedCustomerMobile,
      }));
    }

    const askLocationAfterPageReady = () => {
      if (locationRetryTimerRef.current) {
        window.clearTimeout(locationRetryTimerRef.current);
      }

      locationRetryTimerRef.current = window.setTimeout(() => {
        requestCurrentLocation(true);
      }, 700);
    };

    askLocationAfterPageReady();

    const handlePageShow = () => {
      askLocationAfterPageReady();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        askLocationAfterPageReady();
      }
    };

    window.addEventListener("pageshow", handlePageShow);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      if (locationRetryTimerRef.current) {
        window.clearTimeout(locationRetryTimerRef.current);
      }

      window.removeEventListener("pageshow", handlePageShow);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const filteredVendors = useMemo(() => {
    const value = search.trim().toLowerCase();

    return vendors.filter((vendor) => {
      const vendorName = (vendor.name || "").toLowerCase();
      const vendorCategory = (vendor.category || "").toLowerCase();
      const vendorOffer = (vendor.offer || "").toLowerCase();

      const matchesSearch =
        !value ||
        vendorName.includes(value) ||
        vendorCategory.includes(value) ||
        vendorOffer.includes(value);

      const matchesFilter =
        activeFilter === "All" ||
        (activeFilter === "Pure Veg" && vendor.badgeType === "veg") ||
        (activeFilter === "Offers" && vendor.offer) ||
        (activeFilter === "Open Now" && vendor.status === "Open Now") ||
        activeFilter === "Delivery" ||
        activeFilter === "Under ₹99";

      return matchesSearch && matchesFilter;
    });
  }, [search, activeFilter, vendors]);

  const handleUseLocation = () => {
    requestCurrentLocation(false);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();

    const mobile = profileForm.mobile.trim();

    if (!mobile) {
      alert("Mobile number required hai.");
      return;
    }

    if (mobile.length < 10) {
      alert("Valid mobile number dalo.");
      return;
    }

    try {
      const response = await fetch("/api/public/food-radar/customers/register", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...profileForm,
          mobile,
          latitude: customerLocation?.latitude || null,
          longitude: customerLocation?.longitude || null,
          area: locationStatus,
          city: "Mumbai",
        }),
      });

      const data = await response.json();

      if (!data.success) {
        alert(data.message || "Profile save nahi hua.");
        return;
      }

      setCustomer(data.customer);
      localStorage.setItem("foodRadarCustomerId", data.customer.id);
      localStorage.setItem("foodRadarCustomerMobile", data.customer.mobile);

      setShowProfileModal(false);
      alert("Profile save ho gaya.");
    } catch (error) {
      console.error("Food Radar profile save error:", error);
      alert("Server error. Profile save nahi hua.");
    }
  };

  const handleVendorOpen = (vendor) => {
    if (locationRequired) {
      alert("Please allow your location to show nearby food offers.");
      return;
    }

    const vendorId = getVendorId(vendor);

    if (!vendorId) {
      alert("Vendor id missing hai. Order page open nahi ho sakta.");
      return;
    }

    /*
      Direct Home -> Order flow me bhi current shop ka coin context save karte
      hain. FoodRadarOrderForm actual balance API se dobara load karega aur
      backend DB se final validation karega. Ye value sirf display fallback hai.
      Coins automatic apply nahi honge; customer khud input me amount dalega.
    */
    const availableCoins =
      vendor?.available_coins === null ||
      vendor?.available_coins === undefined
        ? 0
        : Math.max(
            0,
            parseInt(vendor.available_coins || 0, 10) || 0
          );

    sessionStorage.setItem(
      "foodRadarRedeemVendor",
      String(vendorId)
    );
    sessionStorage.setItem(
      "foodRadarRedeemAvailableCoins",
      String(availableCoins)
    );
    sessionStorage.setItem(
      "foodRadarOrderEntrySource",
      "home"
    );

    window.location.href = `/food-radar/order/${vendorId}`;
  };


  const openRewards = () => {
    const savedCustomerId =
      localStorage.getItem("foodRadarCustomerId");

    if (!savedCustomerId) {
      setShowProfileModal(true);
      return;
    }

    window.location.href = "/food-radar/rewards";
  };

  if (initialLocationLoading) {
    return (
      <div
        className="food-radar-page"
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          padding: "24px",
          textAlign: "center",
          background: "linear-gradient(180deg, #fff7ed 0%, #ffffff 100%)",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: "360px",
            borderRadius: "28px",
            padding: "28px 22px",
            background: "#ffffff",
            boxShadow: "0 20px 55px rgba(15, 23, 42, 0.12)",
            border: "1px solid rgba(249, 115, 22, 0.16)",
          }}
        >
          <div
            style={{
              width: "72px",
              height: "72px",
              borderRadius: "24px",
              margin: "0 auto 16px",
              display: "grid",
              placeItems: "center",
              fontSize: "34px",
              background: "linear-gradient(135deg, #f97316, #ef4444)",
              color: "#ffffff",
            }}
          >
            📍
          </div>

          <p
            style={{
              margin: "0 0 6px",
              fontSize: "13px",
              fontWeight: "800",
              color: "#f97316",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            VAKRA Food Radar
          </p>

          <h2
            style={{
              margin: "0 0 10px",
              fontSize: "22px",
              color: "#111827",
            }}
          >
            Loading...
          </h2>

          <p
            style={{
              margin: "0",
              fontSize: "14px",
              lineHeight: "1.6",
              color: "#6b7280",
            }}
          >
            Current location detect ho raha hai. Nearby food offers dikhane ke liye
            location allow karein.
          </p>

          <div
            style={{
              margin: "20px auto 0",
              width: "46px",
              height: "46px",
              borderRadius: "50%",
              border: "4px solid #fed7aa",
              borderTopColor: "#f97316",
              animation: "frSpin 0.9s linear infinite",
            }}
          />

          <style>
            {`
              @keyframes frSpin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
              }
            `}
          </style>
        </div>
      </div>
    );
  }

  return (
    <div className="food-radar-page">
      <FoodRadarForegroundNotification />
      <header className="fr-header">
        <div className="fr-brand-row">
          <div className="fr-logo">📍</div>
          <div>
            <h1>VAKRA Food Radar</h1>
            <p>Nearby food offers around you</p>
          </div>
        </div>

        <button className="fr-bell-btn" type="button" aria-label="Notifications">
          🔔
          <span>3</span>
        </button>
      </header>

      <main className="fr-main">
        <section className="fr-location-card">
          <div>
            <p className="fr-small-label">Your Location</p>
            <h2>📍 {locationStatus}</h2>
            <p>
              {locationRequired
                ? "Please allow your location to show nearby food offers."
                : "Aapke aas-paas ke food offers yahan dikhenge."}
            </p>
          </div>
          <button type="button" onClick={handleUseLocation}>
            {locationRequired ? "Allow Location" : "Use My Location"}
          </button>
        </section>

        <section className="fr-search-section">
          <div className="fr-search-box">
            <span>🔎</span>
            <input
              type="text"
              placeholder="Search food, restaurant, offer..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              disabled={locationRequired}
            />
          </div>

          <div className="fr-filter-row">
            {filters.map((filter) => (
              <button
                key={filter}
                type="button"
                className={activeFilter === filter ? "active" : ""}
                onClick={() => setActiveFilter(filter)}
                disabled={locationRequired}
              >
                {filter}
              </button>
            ))}
          </div>
        </section>

        <section className="fr-hero-banner">
          <div>
            <p>🔥 Food Drops Near You</p>
            <h2>
              {locationRequired
                ? "Allow location to unlock nearby food offers"
                : "Live offers from nearby local shops"}
            </h2>
            <span>
              {locationRequired
                ? "Location permission ke baad nearby restaurants show honge."
                : "Fresh deals. Real savings."}
            </span>
          </div>
          <div className="fr-hero-icon">🍔</div>
        </section>

        <section className="fr-feature-grid">
          <div className="fr-feature-card">
            <span>📍</span>
            <strong>Nearby Offers</strong>
            <p>Aas-paas ke live food deals.</p>
          </div>
          <div className="fr-feature-card">
            <span>🍽️</span>
            <strong>Restaurants</strong>
            <p>Fast food aur local shops search.</p>
          </div>
          <div className="fr-feature-card">
            <span>🎟️</span>
            <strong>Coupons</strong>
            <p>Offers aur rewards yahan milenge.</p>
          </div>
          <div className="fr-feature-card">
            <span>📦</span>
            <strong>Track Order</strong>
            <p>Order status live check karein.</p>
          </div>
        </section>

        <section className="fr-section-head">
          <div>
            <p>Open Now</p>
            <h2>Nearby Restaurants</h2>
          </div>
          <button
            type="button"
            onClick={() =>
              customerLocation ? loadVendors(customerLocation) : requestCurrentLocation(false)
            }
          >
            {loadingVendors ? "Loading..." : locationRequired ? "Allow" : "Refresh"}
          </button>
        </section>

        <section className="fr-vendor-list">
          {locationRequired ? (
            <div className="fr-empty-state">
              <h3>Location Required</h3>
              <p>Please allow your location to show nearby food offers and restaurants.</p>
              <button type="button" onClick={handleUseLocation}>
                Allow Location
              </button>
            </div>
          ) : loadingVendors ? (
            <div className="fr-empty-state">
              <h3>Restaurants load ho rahe hain...</h3>
              <p>Thoda wait karein.</p>
            </div>
          ) : filteredVendors.length === 0 ? (
            <div className="fr-empty-state">
              <h3>Koi result nahi mila</h3>
              <p>Aapke current location ke paas abhi koi vendor nahi mila.</p>
            </div>
          ) : (
            filteredVendors.map((vendor) => {
              const vendorImageSrc = getVendorImageSrc(vendor);

              return (
                <article
                  className="fr-vendor-card fr-vendor-card-compact"
                  key={vendor.id}
                >
                  <button
                    type="button"
                    className="fr-vendor-img-btn"
                    onClick={() => handleVendorOpen(vendor)}
                  >
                    <div className={`fr-vendor-img ${vendor.color || "orange"}`}>
                      {vendorImageSrc ? (
                        <img
                          src={vendorImageSrc}
                          alt={vendor.name}
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                            const fallback =
                              e.currentTarget.parentElement.querySelector(
                                ".fr-img-fallback"
                              );
                            if (fallback) fallback.style.display = "grid";
                          }}
                        />
                      ) : null}

                      <span
                        className="fr-img-fallback"
                        style={{
                          display: vendorImageSrc ? "none" : "grid",
                        }}
                        aria-label="Shop image unavailable"
                      />
                    </div>
                  </button>

                  <div className="fr-vendor-info">
                    <div className="fr-vendor-top fr-vendor-top-compact">
                      <div className="fr-vendor-title-block">
                        <h3>{vendor.name}</h3>

                        <div className="fr-rating-line-home">
                          <span className="fr-rating-star-home">★</span>

                          {Number(vendor.average_rating || 0) > 0 &&
                          Number(vendor.rating_count || 0) > 0 ? (
                            <>
                              <strong className="fr-rating-stars-text">
                                {renderRatingStars(vendor.average_rating)}
                              </strong>
                              <span className="fr-rating-number">
                                {Number(vendor.average_rating).toFixed(1)}
                              </span>
                              <span>
                                ({Number(vendor.rating_count)} ratings)
                              </span>
                            </>
                          ) : (
                            <>
                              <strong className="fr-rating-stars-text empty">
                                ☆☆☆☆☆
                              </strong>
                              <span>No ratings yet</span>
                            </>
                          )}

                          {vendor.distance ? (
                            <>
                              <i>•</i>
                              <span>📍 {vendor.distance}</span>
                            </>
                          ) : null}
                        </div>
                      </div>

                      <span
                        className={`fr-status-chip ${
                          vendor.status === "Open Now" ? "open" : "closing"
                        }`}
                      >
                        {vendor.status || "Open Now"}
                      </span>
                    </div>

                    {customer?.id ? (
                      <button
                        type="button"
                        className="fr-home-coin-row"
                        onClick={openRewards}
                      >
                        <span className="fr-home-coin-icon">🪙</span>

                        <strong>
                          {vendor.available_coins === null ||
                          vendor.available_coins === undefined
                            ? "Coins unavailable"
                            : `You have ${Number(vendor.available_coins || 0)} coins`}
                        </strong>

                        {Number(vendor.pending_coins || 0) > 0 ? (
                          <small>
                            +{Number(vendor.pending_coins)} pending
                          </small>
                        ) : null}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="fr-home-coin-row login"
                        onClick={() => setShowProfileModal(true)}
                      >
                        <span className="fr-home-coin-icon">🪙</span>
                        <strong>Login to see your coins</strong>
                      </button>
                    )}

                    {vendor.offer ? (
                      <div className="fr-home-offer-mini">
                        <strong>{vendor.offer}</strong>
                        {vendor.offerNote ? <span>{vendor.offerNote}</span> : null}
                      </div>
                    ) : null}

                    <div className="fr-card-actions fr-card-actions-compact">
                      <button
                        type="button"
                        onClick={() => handleVendorOpen(vendor)}
                      >
                        Order
                      </button>
                    </div>
                  </div>
                </article>
              );
            })
          )}
        </section>
      </main>

      <nav className="fr-bottom-nav">
        <button type="button" className="active">
          <span>🏠</span>
          Home
        </button>
        <button
  type="button"
  disabled={locationRequired}
  onClick={() => {
    if (!customerLocation) {
      alert("Please allow your location first.");
      return;
    }

    window.location.href =
      `/food-radar/offers?lat=${customerLocation.latitude}&lng=${customerLocation.longitude}`;
  }}
>
  <span>🎁</span>
  Offers
</button>
        <button type="button" onClick={openRewards}>
          <span>🪙</span>
          Rewards
        </button>
        <button type="button">
          <span>📦</span>
          Orders
        </button>
        <button type="button" onClick={() => setShowProfileModal(true)}>
          <span>👤</span>
          {customer?.mobile ? "Profile" : "Login"}
        </button>
      </nav>

      {showProfileModal && (
        <div className="fr-modal-overlay">
          <div className="fr-profile-modal">
            <div className="fr-modal-head">
              <div>
                <h2>Customer Profile</h2>
                <p>Apni details save karein.</p>
              </div>
              <button type="button" onClick={() => setShowProfileModal(false)}>
                ✕
              </button>
            </div>

            <form onSubmit={handleSaveProfile} className="fr-profile-form">
              <label>
                Name
                <input
                  type="text"
                  placeholder="Your name"
                  value={profileForm.name}
                  onChange={(e) =>
                    setProfileForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                />
              </label>

              <label>
                Mobile Number *
                <input
                  type="tel"
                  placeholder="10 digit mobile number"
                  value={profileForm.mobile}
                  onChange={(e) =>
                    setProfileForm((prev) => ({ ...prev, mobile: e.target.value }))
                  }
                />
              </label>

              <label>
                Email Optional
                <input
                  type="email"
                  placeholder="Email"
                  value={profileForm.email}
                  onChange={(e) =>
                    setProfileForm((prev) => ({ ...prev, email: e.target.value }))
                  }
                />
              </label>

              <label>
                Address Optional
                <textarea
                  placeholder="Home / work address"
                  value={profileForm.default_address}
                  onChange={(e) =>
                    setProfileForm((prev) => ({
                      ...prev,
                      default_address: e.target.value,
                    }))
                  }
                />
              </label>

              {customerLocation && (
                <p className="fr-location-note">
                  Location saved: {customerLocation.latitude.toFixed(5)},{" "}
                  {customerLocation.longitude.toFixed(5)}
                </p>
              )}

              {customer && (
                <p className="fr-location-note">Logged as: {customer.mobile}</p>
              )}

              <button type="submit" className="fr-save-profile-btn">
                Save Profile
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default FoodRadarHomePage;
